import { Controller, useFormContext, useWatch } from "react-hook-form";

import { ChangeCircle } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { PromotionCampaignSelect } from "@/features/promotion-campaigns";
import {
  PromotionCampaignStatusEnum,
} from "@/features/promotion-campaigns/status";
import {
  CustomInputField,
  CustomNumberField,
  CustomSelect,
} from "@/features/ui";
import { DiscountTypeEnum } from "../enums/discount-type-enum";
import {
  PromotionTypeEnum,
  promotionTypeLabels,
} from "../enums/promotion-type-enum";
import { PromotionStatusEnum } from "../status";
import { promotionStatusLabels } from "../status/status-labels";

const promotionTypeOptions = Object.values(PromotionTypeEnum).map((value) => ({
  value,
  label: promotionTypeLabels[value],
}));

const statusOptions = [
  PromotionStatusEnum.DRAFT,
  PromotionStatusEnum.ACTIVE,
  PromotionStatusEnum.PAUSED,
].map((value) => ({
  value,
  label: promotionStatusLabels[value],
}));

const GeneralInformationSection = () => {
  const { control, setValue } = useFormContext();
  const discountType = useWatch({ control, name: "discountType" });

  const toggleDiscountType = () => {
    setValue(
      "discountType",
      discountType === DiscountTypeEnum.FIXED_AMOUNT
        ? DiscountTypeEnum.PERCENTAGE
        : DiscountTypeEnum.FIXED_AMOUNT,
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Informasi Umum
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <CustomInputField
                {...field}
                label="Nama Promosi"
                required
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name="promotionCampaign"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PromotionCampaignSelect
                {...field}
                statuses={[
                  PromotionCampaignStatusEnum.DRAFT,
                  PromotionCampaignStatusEnum.ACTIVE,
                ]}
                TextFieldProps={{
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name="promotionType"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <CustomSelect
                {...field}
                label="Tipe Promosi"
                options={promotionTypeOptions}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name="status"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <CustomSelect
                {...field}
                label="Status"
                options={statusOptions}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name="discountAmount"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box sx={{ display: "flex", alignItems: "end" }}>
                <CustomNumberField
                  {...field}
                  label="Nilai Diskon"
                  required
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    startAdornment:
                      discountType === DiscountTypeEnum.FIXED_AMOUNT ? (
                        <InputAdornment position="start">Rp</InputAdornment>
                      ) : null,
                    endAdornment:
                      discountType === DiscountTypeEnum.PERCENTAGE ? (
                        <InputAdornment position="end">%</InputAdornment>
                      ) : null,
                  }}
                  inputProps={{ style: { textAlign: "right" } }}
                />
                <IconButton
                  onClick={toggleDiscountType}
                  aria-label="Toggle Discount Type"
                >
                  <ChangeCircle />
                </IconButton>
              </Box>
            )}
          />
        </Grid>
        {discountType === DiscountTypeEnum.PERCENTAGE && (
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Controller
              name="maximumDiscountAmount"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <CustomNumberField
                  {...field}
                  label="Maksimal Diskon"
                  placeholder="Tanpa batas"
                  error={!!error}
                  helperText={
                    error?.message || "Kosongkan jika tanpa batas maksimal"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">Rp</InputAdornment>
                    ),
                  }}
                  inputProps={{
                    decimalScale: 0,
                    style: { textAlign: "right" },
                  }}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default GeneralInformationSection;
